/* eslint-disable no-console */
import { InputError } from "@app/components/core/InputError/InputError"
import { Separator } from "@app/components/core/Separator/Separator"
import Button from "@app/components/move2Library/Button/Button"
import Input from "@app/components/move2Library/Input/Input"
import { useRaiseError } from "@app/store/actions/errorActions"
import { removePriceAction as __removePriceAction, addPriceAction } from "@app/store/actions/orderActions"
import { useGetStrings } from "@app/store/service/cmsService"
import { useCheckScanCode } from "@app/store/service/connectorService"
import { appStore } from "@app/store/store"
import { ORDER_LINE_TYPES, SCANCODE_TYPES } from "@app/utils/constants"
import { getPriceString } from "@app/utils/localeUtils"
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle"
import { faX } from "@fortawesome/free-solid-svg-icons/faX"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Tooltip } from "@tecreation/funnel-components"
import { useSelector } from "@tecreation/js-state-react"
import { useEffect, useRef, useState } from "react"
import OrderLines from "../OrderLines/OrderLines"

const getActionDescription = action => {
	if (action.scanCodeTypeId === SCANCODE_TYPES.TICKET) {
		return (
			<span>
				Ticket: <span className="color-fg-secondary semibold">{action.scanCode}</span>
			</span>
		)
	}
	if (action.scanCodeTypeId === SCANCODE_TYPES.PRICEACTION) {
		return (
			<span>
				{action.priceAction} - {action.scanCode}
			</span>
		)
	}
	if (action.scanCodeTypeId === SCANCODE_TYPES.VOUCHER) {
		return (
			<span>
				{action.voucher}
				{action.scanCode ? " - " + action.scanCode : ""}
			</span>
		)
	}
}

const VoucherLine = ({ action, onDelete, removeButtonTranslation }) => {
	const formattedOrder = useSelector(state => state.order.formattedOrder)
	let price = undefined

	if (formattedOrder && action.scanCodeTypeId === SCANCODE_TYPES.TICKET) {
		for (let line of formattedOrder.orderLines) {
			let fd = line.details[0]
			if (fd.scanCode && fd.scanCode === action.scanCode) {
				price = price === undefined ? 0 : price
				price += fd.amounts[0].grossAmount
			}
		}
	}

	if (formattedOrder && action.scanCodeTypeId === SCANCODE_TYPES.VOUCHER && action.details) {
		let vfd = action.details[0].voucherDetailToken
		price = 0
		for (let line of formattedOrder.orderLines) {
			let fd = line.details[0]
			if (fd.typeId === ORDER_LINE_TYPES.VoucherDetail && vfd === fd.voucherDetailToken) {
				price = fd.amounts[0].grossAmount / fd.quantity
				break
			}
			//Voucher like Entrace + Doppeliege can not be matched over the voucherDetailToken because it's used like a value voucher so we need to map over the scanCode
			if (fd.typeId === ORDER_LINE_TYPES.VoucherDetail && action.scanCode === fd.scanCode) {
				price += fd.amounts[0].grossAmount / fd.quantity
			}
		}
	}

	let description = getActionDescription(action)
	return (
		<li>
			<div className="flex flex-justify-between mt-3 f5">
				<div className="">
					<div>{description}</div>
					<div className="ml-0 f6">
						{action.links?.map(e => {
							if (e.typeId === ORDER_LINE_TYPES.Activity) {
								return <p>{e.activity}</p>
							} else if (e.typeId === ORDER_LINE_TYPES.Object || e.typeId === ORDER_LINE_TYPES.ObjectGroup) {
								return <p>{e.zoneAbbrevation + "-" + e.abbreviation + e.number}</p>
							}
						})}
					</div>
				</div>
				<div className="flex flex-items-center">
					{price && <div className="regular pr-3">{getPriceString(price, undefined, false)}</div>}
					{(!action.scanCode || !action.scanCode.startsWith(appStore.get("settings.htgGuestCardCode"))) && (
						<button onClick={() => onDelete(action)} className="delete-btn p-2 clickable" aria-label={removeButtonTranslation}>
							<FontAwesomeIcon icon={faX} />
						</button>
					)}
				</div>
			</div>
		</li>
	)
}

export default function Vouchers({ formattedOrder, onAction }) {
	const [scanCode, setScanCode] = useState(undefined)
	const inputRef = useRef(null)
	const priceActions = useSelector(state => state.order.priceActions)
	const { data: strings } = useGetStrings()
	const {
		orderOverviewVouchers,
		orderOverviewVouchersSubText,
		orderOverviewVoucherTooltip,
		orderOverviewVoucherMessage,
		buttonRedeem,
		moreInformation,
		buttonLabelRemoveVoucher,
		inputLabelVoucherCode,
		addedVouchersLabel,
	} = strings || {}
	const raiseError = useRaiseError()

	const hasThermencardLine = formattedOrder.orderLines.filter(e => e.details[0].typeId === ORDER_LINE_TYPES.Account)?.length ? true : false

	/**
	 * Chris told me to just use the first date
	 */
	const [checkScanCode, { isFetching: scanCodeLoading, data: voucherData, error: voucherError }] = useCheckScanCode()

	const removePriceAction = action => {
		__removePriceAction(action)
		onAction()
	}
	useEffect(() => {
		if (voucherData && !voucherError) {
			let order = appStore.get("order")
			console.log(order)
			const applyData = () => {
				if (addPriceAction(voucherData)) {
					inputRef.current.value = ""
					onAction(voucherData)
					setScanCode("")
				} else {
					console.error("voucher code already used")
				}
			}

			if (voucherData.scanCodeTypeId === 5) {
				const orderLines = order?.formattedOrder?.orderLines || []

				const isActivity = orderLines.some(line => line.typeId === 9)

				if (isActivity) {
					return raiseError(new Error("err.EntranceTicketNotAllowedForPayment"))
				}
			}
			applyData()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [voucherData, voucherError])

	useEffect(() => {
		if (voucherError) {
			if (voucherError.response && voucherError.response.data) {
				raiseError(voucherError)
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [voucherError])

	return (
		<div className="mt-4">
			<div className="f4 regular" role="heading" aria-level={2}>
				{orderOverviewVouchers}
				<Tooltip position="top" text={orderOverviewVoucherTooltip} showArrow>
					<span className="ml-2" tabIndex={0} role="button" aria-label={moreInformation}>
						<FontAwesomeIcon icon={faQuestionCircle} />
					</span>
				</Tooltip>
			</div>
			<Separator />

			<div className="color-bg-secondary-100">
				{!hasThermencardLine && <div className="p-3 pb-0 f5">{orderOverviewVouchersSubText}</div>}

				{hasThermencardLine && (
					<div className="p-3 pb-0 f5 color-fg-red-700 semibold text-center">Gutscheine können bei Thermencard Aufladungen nicht eingelöst werden.</div>
				)}

				<OrderLines listTag={false}>
					<div className="flex flex-items-center">
						<Input
							inputRef={inputRef}
							name="voucherCode"
							className="color-bg-default primary"
							disabled={hasThermencardLine || scanCodeLoading}
							onChange={() => {
								if (inputRef.current.value.length === 0) {
									setScanCode("")
								}
							}}
							label={inputLabelVoucherCode}
							hideLabel
						/>
						<Button
							style="secondary"
							isLoading={scanCodeLoading}
							disabled={hasThermencardLine}
							cssStyle={{ height: "40px", transform: "translateX(8px)" }}
							onClick={() => {
								if (inputRef.current.value === scanCode) {
									console.error("tried to input the same scanCode as before")
								}
								setScanCode(inputRef.current.value)
								if (inputRef.current.value.length <= 3) {
									return
								}

								checkScanCode({ scanCode: inputRef.current.value })
							}}
							className="rounded-left-0"
						>
							{buttonRedeem}
						</Button>
					</div>
					{voucherError && scanCode.length && <InputError message={orderOverviewVoucherMessage} />}

					<ol aria-label={addedVouchersLabel}>
						{priceActions.map((e, i) => (
							<VoucherLine key={i} action={e} onDelete={removePriceAction} removeButtonTranslation={buttonLabelRemoveVoucher} />
						))}
						{/* {formattedOrder.priceActionCodes.map((code, index) => <VoucherLine key={index} code={code} onDelete={removePriceAction} />)} */}
					</ol>
				</OrderLines>
			</div>
		</div>
	)
}
